import type { QueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth.ts';
import { useRealtimeInvalidation } from '../realtime/useRealtimeInvalidation.ts';
import type { RealtimeMessage } from '../realtime/transport.ts';
import type { Command, CommandStatus } from './useCommands.ts';

// Live command channel for one robot (push, not poll). Transport is MQTT-over-
// WebSocket to AWS IoT Core (PRD-0006 D10), behind useRealtimeInvalidation. On
// each pushed event the command queries are refreshed:
//
//   fleet/{tenant}/{robot}/cmd     → a newly-issued command (agent acts on these)
//   fleet/{tenant}/{robot}/status  → a lifecycle transition (sent→acked→…)
//
// `cmd` events are published by the backend AFTER the DB commit, so plain
// invalidation is race-free there. `status` events come straight FROM THE ROBOT
// and beat the ingest path's DB write, so those are patched into the query
// cache directly (forward-only, see below) with a delayed invalidation as the
// REST reconcile — otherwise `acked` only ever surfaced on the slow poll.
//
// Fallback is POLLING, never SSE. When IoT realtime isn't configured or the MQTT
// connection is down, the query hooks' refetchInterval (useCommands) keeps the UI
// fresh on a slow cadence. There is deliberately no EventSource/SSE path — SSE
// doesn't work on Lambda (it holds the connection to the function timeout).
//
// The tenant comes from the verified /api/auth/me identity so the topic filter
// lands inside the authorizer's tenant-scoped policy.

// Lifecycle order; a pushed status may only move a command forward. Terminal
// states share a rank — the reconcile refetch settles any disagreement from
// the DB, which stays authoritative.
const STATUS_RANK: Record<CommandStatus, number> = {
  pending: 0, sent: 1, acked: 2, completed: 3, failed: 3, cancelled: 3,
};

interface StatusEvent {
  command_id: number;
  robot_id?: string;
  status: CommandStatus;
  result?: Record<string, unknown>;
}

function asStatusEvent(msg: RealtimeMessage): StatusEvent | null {
  if (!msg.topic.endsWith('/status')) return null;
  const p = msg.payload as Partial<StatusEvent> | undefined;
  if (typeof p?.command_id !== 'number') return null;
  if (typeof p.status !== 'string' || !(p.status in STATUS_RANK)) return null;
  return p as StatusEvent;
}

// Patch the robot-pushed transition into the cached command lists. Returns
// true when something was actually updated (the caller then defers its
// invalidation); false falls back to immediate invalidation — e.g. the
// command isn't in the cache yet.
function applyStatusToCache(qc: QueryClient, msg: RealtimeMessage): boolean {
  const ev = asStatusEvent(msg);
  if (!ev) return false;
  let patched = false;
  const patchList = (key: readonly unknown[]) => {
    qc.setQueryData<Command[]>(key as unknown[], (old) => {
      if (!old) return old;
      let hit = false;
      const next = old.map((c) => {
        if (c.id !== ev.command_id) return c;
        if (STATUS_RANK[ev.status] <= STATUS_RANK[c.status]) return c;
        hit = true;
        return { ...c, status: ev.status, result: ev.result ?? c.result };
      });
      if (hit) patched = true;
      return hit ? next : old;
    });
  };
  if (ev.robot_id) patchList(['commands', ev.robot_id]);
  patchList(['commands', 'pending']);
  return patched;
}
export function useCommandStream(robotId: string | undefined): { connected: boolean } {
  const { data } = useAuth();
  const tenant = data?.user?.tenant;

  const rt = useRealtimeInvalidation({
    topicFilters: tenant && robotId ? [`fleet/${tenant}/${robotId}/#`] : [],
    keysFor: (msg) => {
      // EVERY frame on the robot's subtree refreshes the command queues, not
      // just `/status` ones. That looks redundant next to applyStatusToCache,
      // but it is the mechanism that actually kept acks live: the robot's 5 s
      // telemetry heartbeat re-reads the command list, which covers a dropped
      // or unparsed `/status` frame and a poll paused because the window is
      // unfocused. Routing telemetry away from these keys (#166) is exactly
      // how acks stopped appearing without a manual refresh.
      const keys: unknown[][] = [['commands', robotId], ['commands', 'pending']];
      if (msg.topic.endsWith('/telemetry')) keys.push(['telemetry', robotId]);
      return keys;
    },
    applyToCache: applyStatusToCache,
    enabled: Boolean(tenant && robotId),
  });

  return { connected: rt.connected };
}

// Fleet-wide live command feed for the operator console. Subscribes to the whole
// tenant subtree so an issue or transition on ANY robot lands live; the affected
// robot's per-robot query is invalidated too (payload carries robot_id). Same
// MQTT-or-polling model — no SSE.
export function useFleetCommandStream(): { connected: boolean } {
  const { data } = useAuth();
  const tenant = data?.user?.tenant;

  const rt = useRealtimeInvalidation({
    topicFilters: tenant ? [`fleet/${tenant}/#`] : [],
    keysFor: (msg) => {
      const keys: unknown[][] = [['commands', 'pending']];
      const robotId = (msg.payload as { robot_id?: string } | undefined)?.robot_id;
      if (robotId) keys.push(['commands', robotId]);
      return keys;
    },
    applyToCache: applyStatusToCache,
    enabled: Boolean(tenant),
  });

  return { connected: rt.connected };
}
